import { type User } from "@internal/user";
import {
  type GetSalesReportV3Augmented_me_salesTableReportV3Augmented_actual_rows,
  type SalesPageMetric,
} from "@north-beam/nb-client-side";
import {
  type DbForecastV1DateRow,
  metrics as metricsPkg,
} from "@north-beam/nb-common";

import { getDateOnly } from "./chart";

export type ChartDatasets = {
  datasets: {
    series: metricsPkg.MetricSeries;
    name: string | null;
    dimensions: { [dimension: string]: string };
  }[];
};

export const preprocessForChart = (
  nbOnlyMetrics: SalesPageMetric[],
  customMetrics: SalesPageMetric[],
  rows: GetSalesReportV3Augmented_me_salesTableReportV3Augmented_actual_rows[],
  timezone: User["timezone"],
): ChartDatasets => {
  const datasets: ChartDatasets["datasets"] = [];

  for (const dataset of rows) {
    const { dailyMetrics, forecastV1Daily, name, dimensions } = dataset;
    if (!dailyMetrics) {
      continue;
    }
    const generatedDataset: ChartDatasets["datasets"][number] = {
      series: {},
      name,
      dimensions,
    };

    const dateToForecastRow: Record<string, DbForecastV1DateRow> = {};
    if (forecastV1Daily) {
      for (const forecastRow of forecastV1Daily) {
        dateToForecastRow[forecastRow.date] = forecastRow;
      }
    }

    for (const dailyMetricPoint of dailyMetrics) {
      const metricPoint = metricsPkg.makeMetricPoint(
        dailyMetricPoint,
        dateToForecastRow[dailyMetricPoint.date],
      );

      nbOnlyMetrics.forEach((metric) => {
        metricPoint[metric.id as keyof metricsPkg.MetricPoint] =
          metric.calculate(metricPoint) as any;
      });

      // Needs all metrics to be calculated before calculating custom metrics
      customMetrics.forEach((metric) => {
        metricPoint[metric.id as keyof metricsPkg.MetricPoint] =
          metric.calculate(metricPoint as any) as any;
      });

      const startOfDay = getDateOnly(
        dailyMetricPoint.date,
        timezone,
      ).toISOString();
      generatedDataset.series[startOfDay] = metricPoint;
    }

    datasets.push(generatedDataset);
  }

  return { datasets };
};
